import './skeleton.css'

interface MutualFundListItemSkeletonProps {
  /**
   * Whether to show a border at the bottom
   * @default true
   */
  borderRequired?: boolean
  /**
   * Whether this is the last item in the list
   * @default false
   */
  isLast?: boolean
  /**
   * Whether to show the right chevron (navigation indicator)
   * @default true
   */
  showChevron?: boolean
  /**
   * Whether to show a tag at the top (for NEW/UPDATED status)
   * @default false
   */
  showTag?: boolean
}

/**
 * Skeleton loader component for MutualFundListItem
 *
 * Displays a loading placeholder that matches the structure and layout
 * of the MutualFundListItem component.
 *
 * @example
 * // Basic usage
 * <MutualFundListItemSkeleton />
 *
 * @example
 * // With custom props
 * <MutualFundListItemSkeleton
 *   showTag={true}
 *   showChevron={true}
 *   isLast={false}
 * />
 *
 * @example
 * // Multiple skeleton items
 * {[...Array(3)].map((_, index) => (
 *   <MutualFundListItemSkeleton
 *     key={index}
 *     isLast={index === 2}
 *   />
 * ))}
 */
export default function MutualFundListItemSkeleton({
  borderRequired = true,
  isLast = false,
  showTag = false
}: MutualFundListItemSkeletonProps) {
  return (
    <div className='flex flex-row justify-center items-center w-full'>
      {/* Logo placeholder */}
      <div className={`${showTag ? 'h-20' : 'h-10'} w-10 self-start ml-5 mt-4 flex-shrink-0`}>
        <div className='skeleton h-full w-full rounded-md'></div>
      </div>

      {/* Main content area */}
      <div
        className={`flex flex-col w-full pt-[18px] pb-5 min-h-[107px] ${borderRequired && !isLast && 'border-b border-grey80'} ml-5`}
      >
        {/* Tag placeholder (optional) */}
        {showTag && (
          <div className='mb-1'>
            <div className='skeleton h-[16px] w-[40px] rounded-md'></div>
          </div>
        )}

        <div>
          <div>
            {/* Fund name and category */}
            <div className='flex justify-between'>
              <div className='flex gap-4 w-1 flex-1'>
                <div className='flex flex-col w-full'>
                  {/* Fund name placeholder */}
                  <div className='skeleton h-[18px] w-10/12 rounded-md mb-[2px]'></div>
                  {/* Fund category placeholder */}
                  <div className='skeleton h-[16px] w-[120px] rounded-md'></div>
                </div>
              </div>
            </div>

            {/* Leaf rank and badges row */}
            <div className='flex items-center gap-2 pt-4'>
              {/* Leaf rank placeholder */}
              <div className='flex items-center gap-1'>
                <div className='skeleton h-[16px] w-[16px] rounded'></div>
                <div className='skeleton h-[16px] w-[20px] rounded'></div>
                <div className='skeleton h-[16px] w-[16px] rounded'></div>
              </div>

              {/* Badge placeholder */}
              <div className='skeleton h-[22px] w-[80px] rounded-[8px]'></div>

              {/* Tag placeholder */}
              <div className='skeleton h-[22px] w-[60px] rounded-[8px]'></div>
            </div>

            {/* Additional info placeholder (optional) */}
            <div className='flex flex-col items-start pt-2'>
              <div className='skeleton h-[22px] w-[140px] rounded-[8px]'></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
